/**
 * Phase 101fb (Feb 2026) — Audit intégrité stock (UI)
 *
 * Écran dédié aux réconciliations de lots quand la quantité en base ne colle
 * plus à la somme des mouvements. Motivé par le cas Bœuf basilic doux SKU 207 :
 * 4 unités disparues suite à une suppression de BL avant le fix Phase 101fa++.
 *
 * Workflow :
 *   1. Bouton "Lancer l'audit" → GET /stock/integrity-audit (only_discrepancies)
 *   2. Tableau des lots avec écart, tri par |delta| décroissant
 *   3. Bouton "Détails" → dialog avec l'historique complet des mouvements
 *   4. Bouton "Réconcilier" → dialog avec input target_quantity + reason
 *      → POST /lots/{id}/reconcile (crée un mouvement ADJUST traçable HACCP)
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle, FileSearch, Loader2, Wrench, X, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CH", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function StockIntegrityAudit() {
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null); // { total_lots_scanned, discrepancies_found, lots: [] }
  const [includeArchived, setIncludeArchived] = useState(false);
  const [detailLot, setDetailLot] = useState(null); // détail d'un lot (audit complet avec movements)
  const [detailLoading, setDetailLoading] = useState(false);
  const [reconcileLot, setReconcileLot] = useState(null); // lot en cours de réconciliation
  const [reconcileForm, setReconcileForm] = useState({ target: "", reason: "" });
  const [reconciling, setReconciling] = useState(false);
  const [search, setSearch] = useState("");

  const filteredLots = React.useMemo(() => {
    if (!audit?.lots) return [];
    const q = search.trim().toLowerCase();
    if (!q) return audit.lots;
    return audit.lots.filter((l) =>
      (l.product_name || "").toLowerCase().includes(q)
      || (l.product_sku || "").toLowerCase().includes(q)
      || (l.lot_number || "").toLowerCase().includes(q)
    );
  }, [audit, search]);

  const runAudit = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        only_discrepancies: "true",
        include_archived: includeArchived ? "true" : "false",
      });
      const { data } = await api.get(`/stock/integrity-audit?${params.toString()}`);
      setAudit(data);
      if (data.discrepancies_found === 0) {
        toast.success("✅ Aucun écart détecté — tous les stocks sont cohérents");
      } else {
        toast.warning(`${data.discrepancies_found} lot(s) avec écart détecté(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur audit");
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (lot) => {
    setDetailLoading(true);
    setDetailLot({ ...lot, movements: null });
    try {
      const { data } = await api.get(`/lots/${lot.lot_id}/audit`);
      setDetailLot(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur détail");
      setDetailLot(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const openReconcile = (lot) => {
    setReconcileLot(lot);
    setReconcileForm({
      target: String(lot.theoretical_quantity),
      reason: `Restauration manuelle après audit intégrité (delta=${lot.delta > 0 ? "+" : ""}${lot.delta})`,
    });
  };

  const confirmReconcile = async () => {
    const target = Number(String(reconcileForm.target).replace(",", "."));
    if (!Number.isFinite(target) || target < 0) {
      toast.error("Quantité cible invalide");
      return;
    }
    if (!reconcileForm.reason.trim()) {
      toast.error("Motif obligatoire (traçabilité HACCP)");
      return;
    }
    setReconciling(true);
    try {
      const { data } = await api.post(
        `/lots/${reconcileLot.lot_id}/reconcile?target_quantity=${target}&reason=${encodeURIComponent(reconcileForm.reason)}`
      );
      toast.success(
        `Lot ${reconcileLot.lot_number} réconcilié : ${data.old_quantity} → ${data.new_quantity} unités (mouvement ADJUST créé)`
      );
      setReconcileLot(null);
      await runAudit(); // refresh
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur réconciliation");
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="stock-integrity-audit-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-medium text-[#1A1C1A] flex items-center gap-2">
            <FileSearch className="w-6 h-6 text-[#1F4E3D]" />
            Audit intégrité stock
          </h1>
          <p className="text-sm text-[#575A57] mt-1 max-w-2xl">
            Détecte les lots où la quantité en base ne correspond pas à la somme des mouvements enregistrés
            (fuites silencieuses, BL supprimés avant le fix Phase 101fa++). La réconciliation crée un
            mouvement <code className="text-xs bg-[#F0F0EB] px-1 rounded">ADJUST</code> traçable HACCP,
            jamais de trafic silencieux de la DB.
          </p>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[#1A1C1A]" data-testid="include-archived-toggle">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            <span>Inclure les lots archivés</span>
          </label>
          <Button
            onClick={runAudit}
            disabled={loading}
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
            data-testid="run-audit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSearch className="w-4 h-4 mr-2" />}
            {loading ? "Analyse en cours…" : "Lancer l'audit"}
          </Button>
        </div>
      </div>

      {/* Résumé */}
      {audit && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="audit-summary">
          <Card className="border-[#E5E5E0]">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Lots scannés</div>
              <div className="text-2xl font-medium mt-1 text-[#1A1C1A]">{audit.total_lots_scanned}</div>
            </CardContent>
          </Card>
          <Card className={audit.discrepancies_found > 0 ? "border-[#E54D2E] bg-[#FFF4F1]" : "border-[#88A066] bg-[#F1F6EC]"}>
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Écarts détectés</div>
              <div className="text-2xl font-medium mt-1 flex items-center gap-2">
                {audit.discrepancies_found > 0 ? (
                  <><AlertTriangle className="w-5 h-5 text-[#E54D2E]" /> <span className="text-[#E54D2E]">{audit.discrepancies_found}</span></>
                ) : (
                  <><CheckCircle className="w-5 h-5 text-[#88A066]" /> <span className="text-[#88A066]">0</span></>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5E5E0]">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Tolérance</div>
              <div className="text-sm text-[#575A57] mt-1">± {audit.tolerance} unité (bruit d'arrondi)</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tableau des écarts */}
      {audit && audit.lots.length > 0 && (
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-0">
            <div className="p-3 border-b border-[#E5E5E0]">
              <Input
                placeholder="Rechercher par produit, SKU ou lot… (ex : « bœuf », « SKU-207 »)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-md"
                data-testid="audit-search-input"
              />
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produit / SKU</TableHead>
                  <TableHead>Lot</TableHead>
                  <TableHead className="text-right">Qté réelle</TableHead>
                  <TableHead className="text-right">Qté théorique</TableHead>
                  <TableHead className="text-right">Delta</TableHead>
                  <TableHead className="text-right">Voided OUT</TableHead>
                  <TableHead>DLC</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLots.map((lot) => (
                  <TableRow key={lot.lot_id} data-testid={`audit-row-${lot.lot_number}`}>
                    <TableCell>
                      <div className="font-medium text-[#1A1C1A] flex items-center gap-1.5">
                        {lot.product_name}
                        {lot.archived && (
                          <Badge variant="outline" className="bg-[#F9F9F6] border-[#A3A6A3] text-[#575A57] text-[10px]">
                            archivé
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-[#A3A6A3] font-mono">{lot.product_sku || "—"}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{lot.lot_number}</TableCell>
                    <TableCell className="text-right font-mono">{lot.actual_quantity}</TableCell>
                    <TableCell className="text-right font-mono">{lot.theoretical_quantity}</TableCell>
                    <TableCell className="text-right">
                      <Badge
                        variant="outline"
                        className={lot.delta < 0
                          ? "bg-[#FFF4F1] border-[#E54D2E] text-[#B03B1F] font-mono"
                          : "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F] font-mono"}
                      >
                        {lot.delta > 0 ? <ArrowUp className="w-3 h-3 mr-0.5" /> : <ArrowDown className="w-3 h-3 mr-0.5" />}
                        {lot.delta > 0 ? "+" : ""}{lot.delta}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {lot.voided_units_out > 0 ? (
                        <Badge className="bg-[#FFEEE9] text-[#B03B1F] border border-[#F0A48F]" title="Unités sorties dans un BL supprimé — trace du bug historique">
                          {lot.voided_units_out}
                        </Badge>
                      ) : (
                        <span className="text-xs text-[#A3A6A3]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{lot.dlc || "—"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetail(lot)}
                        data-testid={`audit-detail-${lot.lot_number}`}
                      >
                        Détails
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openReconcile(lot)}
                        className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
                        data-testid={`audit-reconcile-${lot.lot_number}`}
                      >
                        <Wrench className="w-3 h-3 mr-1" />
                        Réconcilier
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {audit && audit.lots.length === 0 && (
        <Card className="border-[#88A066] bg-[#F1F6EC]">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-10 h-10 text-[#88A066] mx-auto mb-3" />
            <div className="text-lg font-medium text-[#1A1C1A]">Aucun écart détecté 🎉</div>
            <div className="text-sm text-[#575A57] mt-1">Tous les stocks sont cohérents.</div>
          </CardContent>
        </Card>
      )}

      {/* Dialog détail lot */}
      <Dialog open={!!detailLot} onOpenChange={(o) => !o && setDetailLot(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle>Détail du lot {detailLot?.lot_number}</DialogTitle>
            <DialogDescription>
              {detailLot?.product_name} · Qté réelle : <strong>{detailLot?.actual_quantity}</strong>
              {" · "}Qté théorique : <strong>{detailLot?.theoretical_quantity}</strong>
              {" · Delta : "}<strong className={detailLot?.delta < 0 ? "text-[#E54D2E]" : "text-[#8C6B2F]"}>
                {detailLot?.delta > 0 ? "+" : ""}{detailLot?.delta}
              </strong>
            </DialogDescription>
          </DialogHeader>
          {detailLoading && (
            <div className="flex justify-center py-6"><Loader2 className="animate-spin w-6 h-6 text-[#1F4E3D]" /></div>
          )}
          {detailLot?.movements && (
            <div className="space-y-3 py-2">
              <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Historique des mouvements</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Qté</TableHead>
                    <TableHead>Motif</TableHead>
                    <TableHead>État</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailLot.movements.map((mv) => (
                    <TableRow key={mv.id} className={mv.voided ? "opacity-60" : ""}>
                      <TableCell className="text-xs">{fmtDate(mv.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          mv.type === "OUT" ? "bg-[#FFF4F1] border-[#E54D2E] text-[#B03B1F]"
                          : mv.type === "IN" ? "bg-[#F1F6EC] border-[#88A066] text-[#5D7746]"
                          : mv.type === "ADJUST" ? "bg-[#EAF3EE] border-[#1F4E3D] text-[#1F4E3D]"
                          : "bg-[#F9F9F6] border-[#E5E5E0] text-[#575A57]"
                        }>{mv.type}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">{mv.quantity}</TableCell>
                      <TableCell className="text-xs">{mv.reason}</TableCell>
                      <TableCell>
                        {mv.voided ? (
                          <Badge variant="outline" className="bg-[#F9F9F6] text-[#A3A6A3] border-[#E5E5E0] text-[10px]">
                            <X className="w-3 h-3 mr-0.5" /> Voidé
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-[#F1F6EC] text-[#5D7746] border-[#B0C79A] text-[10px]">Actif</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {detailLot.voided_units_out > 0 && (
                <div className="p-3 border border-[#F0A48F] bg-[#FFEEE9] rounded text-sm">
                  <strong>{detailLot.voided_units_out} unité(s)</strong> sont sorties dans un ou plusieurs
                  mouvements <em>voidés</em> — signature du bug historique (BL supprimé avant Phase 101fa++).
                  Ces unités devraient être restaurées via « Réconcilier » avec un motif
                  <code className="mx-1 text-xs bg-white px-1 rounded">« Restauration N unités perdues suppression BL »</code>.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog réconciliation */}
      <Dialog open={!!reconcileLot} onOpenChange={(o) => !o && setReconcileLot(null)}>
        <DialogContent data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle>Réconcilier le lot {reconcileLot?.lot_number}</DialogTitle>
            <DialogDescription className="text-xs pt-2">
              {reconcileLot?.product_name}<br />
              Qté actuelle : <strong>{reconcileLot?.actual_quantity}</strong> ·
              Qté théorique : <strong>{reconcileLot?.theoretical_quantity}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1.5 block">Nouvelle quantité cible</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={reconcileForm.target}
                onChange={(e) => setReconcileForm({ ...reconcileForm, target: e.target.value })}
                data-testid="reconcile-target-input"
              />
              <p className="text-xs text-[#575A57] mt-1">
                Par défaut, la valeur théorique. Modifie si tu sais que ta réalité physique diffère
                (ex : casse non-tracée, unités déjà consommées).
              </p>
            </div>
            <div>
              <Label className="mb-1.5 block">Motif (obligatoire — traçabilité HACCP)</Label>
              <Input
                value={reconcileForm.reason}
                onChange={(e) => setReconcileForm({ ...reconcileForm, reason: e.target.value })}
                data-testid="reconcile-reason-input"
              />
            </div>
            <div className="text-[11px] p-2 bg-[#FBF4E9] border border-[#E8D3A8] rounded text-[#8C6B2F]">
              Un mouvement <code>ADJUST</code> sera créé avec ce motif — aucun changement silencieux.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReconcileLot(null)} disabled={reconciling}>Annuler</Button>
            <Button
              onClick={confirmReconcile}
              disabled={reconciling}
              className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
              data-testid="reconcile-confirm-btn"
            >
              {reconciling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
